/**
 * Echo Hunt -- the corpus index pass (P5, §4.1 / §4.2 / §4.3 / §4.4).
 *
 * What this module owns:
 *  1. Grouping source bullets by DREAM, so one LLM call can resolve references across a dream's own
 *     bullets (§4.2) -- while still respecting the visibility matrix (a call sees only its own dream).
 *  2. Turning the model's reply into per-bullet `IndexedBullet` records, with *validation* that
 *     catches the failure modes the JSON schema cannot: missing ordinals, duplicate ordinals,
 *     unknown enum values, out-of-range `oddityCount`, and unknown `filterReason`s.
 *  3. The deterministic POST-PASS (§4.4 + §7): `NormalizeHouseStyle` then `CheckBullet` on each
 *     `selfContainedText`, plus the deterministic filters the model is not trusted with
 *     (`hedgeDominated`, `tooShort`, `bannedTerm` via the checker).
 *  4. `IndexDreamBullets(backend, ...)` -- the whole thing for one dream: call, validate, post-pass,
 *     and (if the post-pass rejects anything repairable) ONE re-ask with the problems spelled out,
 *     then mark whatever still fails as `styleViolation`.
 *
 * Why one re-ask rather than a bounded loop: §P5 says "re-ask once on violations, then mark as
 * filtered `style_violation`". Retrying forever on a bullet whose source text is inherently
 * un-housestyle would burn tokens without converging, and a permanently-filtered bullet is a legible
 * outcome (it shows up in the Corpus tab and can be manually un-filtered).
 *
 * PURE module: no React, no `store`, no DB imports (see README.md). The LLM is passed in as an
 * `LLMBackend`, so tests use `MockLLMBackend`. DB writes live in `Index_Live.ts`.
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §4, §7, §16.2 P5.
 */
import {LLMBackend, CallLLM, CallLLMResult, LLMCallCounter, PreviewText} from "./LLM.js";
import {SourceBullet} from "./Bullets.js";
import {
	BulletEventType,
	BulletEventType_values,
	BulletFilterReason,
	BulletFilterReason_modelValues,
} from "./Types.js";
import {
	CheckBullet,
	CheckBulletOptions,
	CountWords,
	IsHedgeDominated,
	NormalizeHouseStyle,
} from "./Normalize.js";
import {
	Index_JSONSchema,
	Index_SystemPrompt,
	Index_UserPrompt,
} from "./Prompts/IndexPrompt.js";

// shapes
// ==========

/** One bullet's indexed record, before it becomes an `EchoCorpusBullet` row. */
export type IndexedBullet = {
	/** the source bullet this record belongs to (so a caller can join on the stable id) */
	bulletId: string;
	/** standalone rewrite in house style (§4.2, §4.3) */
	selfContainedText: string;
	eventType: BulletEventType;
	/** how many out-of-place objects the core event contains, capped at 4 (§4.1, §5.2) */
	oddityCount: number;
	/** why the bullet is unusable; null = usable (§4.4) */
	filterReason: BulletFilterReason|n;
	/** the post-pass found violations that survived the re-ask; kept for the Corpus tab */
	postPassViolations?: string[]|n;
	/** how many model responses this bullet needed (1 = clean or filtered on the first reply) */
	attempts: number;
};

export type IndexDreamResult = {
	dreamId: string;
	/** one record per input bullet, in input order */
	bullets: IndexedBullet[];
	/** true when the model's reply could never be made valid (all attempts failed) */
	failed: boolean;
	/** human-readable account of what went wrong, for the Corpus tab / logs */
	error?: string|n;
	/** the raw reply of the last attempt (so a dev can see what the model actually said) */
	lastReply?: string|n;
	/** tokens this dream cost, for the per-round/run counter (§13.1) */
	outputTokens: number;
	attempts: number;
};

export type IndexOptions = {
	/** terms that must never appear (§4.4 bannedTerm) */
	bannedWords?: string[]|n;
	/** cliché phrases the rewrite must avoid (§5.5) */
	blacklist?: string[]|n;
	/** model for the `indexer` role; "" lets the backend/settings decide */
	model?: string|n;
	/** per-run tally (§13.1); omit for one-off/dev calls */
	counter?: LLMCallCounter|n;
	/** total model calls per dream, i.e. 1 initial + (maxAttempts - 1) re-asks. Default 2 (§P5). */
	maxAttempts?: number;
};

/** The maximum `oddityCount` accepted from the model. Beyond ~4 the count stops being a useful
 * seeding signal (§5.2), and a runaway value usually means the model misread the field. */
export const MAX_ODDITY_COUNT = 4;

// grouping
// ==========

/** Groups source bullets by dream (`entryId/segmentId`), preserving each dream's bullet order.
 *
 * Exists so `IndexDreamBullets` gets exactly one dream's bullets: the visibility matrix allows a
 * call to see its own source dream and nothing else (§16.0). */
export function GroupBulletsByDream(bullets: SourceBullet[]): Map<string, SourceBullet[]> {
	const byDream = new Map<string, SourceBullet[]>();
	for (const bullet of bullets) {
		const existing = byDream.get(bullet.dreamId);
		if (existing != null) existing.push(bullet);
		else byDream.set(bullet.dreamId, [bullet]);
	}
	return byDream;
}

// reply validation
// ==========

/** One bullet record as it arrives from the model (unvalidated). */
type RawBulletRecord = {
	ordinal?: unknown;
	selfContainedText?: unknown;
	eventType?: unknown;
	oddityCount?: unknown;
	filterReason?: unknown;
};

export type ParseIndexReplyResult = {
	/** per-ordinal records, only for ordinals that were present and usable */
	byOrdinal: Map<number, {selfContainedText: string, eventType: BulletEventType, oddityCount: number, filterReason: BulletFilterReason|n}>;
	/** problems worth telling the model about on a re-ask */
	problems: string[];
};

/** Validates + coerces one model reply. Anything that can be salvaged IS salvaged (a missing
 * `eventType` becomes `other`, an out-of-range `oddityCount` is clamped) because a re-ask is only
 * worth spending on things the model can actually fix. */
export function ParseIndexReply(parsed: unknown, bulletCount: number): ParseIndexReplyResult {
	const byOrdinal = new Map<number, {selfContainedText: string, eventType: BulletEventType, oddityCount: number, filterReason: BulletFilterReason|n}>();
	const problems: string[] = [];

	const rawBullets = (parsed as {bullets?: unknown}|n)?.bullets;
	if (!Array.isArray(rawBullets)) {
		return {byOrdinal, problems: ["The reply's `bullets` field is missing or is not an array."]};
	}

	const seenOrdinals = new Set<number>();
	for (const [i, item] of rawBullets.entries()) {
		const record = item as RawBulletRecord;
		const at = `bullets[${i}]`;

		const ordinal = typeof record.ordinal == "number" ? record.ordinal : Number(record.ordinal);
		if (!Number.isInteger(ordinal)) {
			problems.push(`${at} has no integer \`ordinal\`.`);
			continue;
		}
		if (ordinal < 0 || ordinal >= bulletCount) {
			problems.push(`${at} has ordinal ${ordinal}, which is outside the request's range 0..${bulletCount - 1}.`);
			continue;
		}
		if (seenOrdinals.has(ordinal)) {
			problems.push(`${at} repeats ordinal ${ordinal}; each bullet must appear exactly once.`);
			continue;
		}
		seenOrdinals.add(ordinal);

		const text = typeof record.selfContainedText == "string" ? record.selfContainedText.trim() : "";
		if (text.length == 0) {
			problems.push(`${at} (ordinal ${ordinal}) has an empty \`selfContainedText\`.`);
			continue;
		}

		// eventType: coerce to `other` rather than failing -- the tag only feeds supply-matching (§5.3)
		let eventType: BulletEventType = "other";
		if (typeof record.eventType == "string" && (BulletEventType_values as readonly string[]).includes(record.eventType)) {
			eventType = record.eventType as BulletEventType;
		} else if (record.eventType != null) {
			problems.push(`${at} (ordinal ${ordinal}) has eventType "${String(record.eventType)}", which is not one of: ${BulletEventType_values.join(", ")}.`);
		}

		// oddityCount: clamp into range instead of failing
		const rawOddity = typeof record.oddityCount == "number" ? record.oddityCount : Number(record.oddityCount);
		const oddityCount = Number.isFinite(rawOddity) ? Math.max(0, Math.min(MAX_ODDITY_COUNT, Math.round(rawOddity))) : 0;

		// filterReason: null/absent is the common (usable) case; an unknown string is a real problem
		let filterReason: BulletFilterReason|n = null;
		if (record.filterReason != null && record.filterReason !== "") {
			if (typeof record.filterReason == "string" && (BulletFilterReason_modelValues as readonly string[]).includes(record.filterReason)) {
				filterReason = record.filterReason as BulletFilterReason;
			} else {
				problems.push(`${at} (ordinal ${ordinal}) has filterReason "${String(record.filterReason)}", which is not one of: null, ${BulletFilterReason_modelValues.join(", ")}.`);
			}
		}

		byOrdinal.set(ordinal, {selfContainedText: text, eventType, oddityCount, filterReason});
	}

	// completeness: a reply that silently omits bullets would drop corpus material with no warning
	const missing = [...Array(bulletCount).keys()].filter(a=>!seenOrdinals.has(a));
	if (missing.length > 0) {
		problems.push(`These bullets are missing from the reply (each must appear exactly once): ${missing.join(", ")}.`);
	}

	return {byOrdinal, problems};
}

// the deterministic post-pass
// ==========

/** Runs the deterministic §7 gate over one indexed record and returns the FINAL (possibly changed)
 * record. Behaviour, in order:
 *  1. normalise the text (`NormalizeHouseStyle`, §4.3);
 *  2. if the model already filtered the bullet, keep that verdict (a filter is not overridden by the
 *     text being clean -- e.g. `thoughtOnly` text is perfectly well-written);
 *  3. otherwise apply the deterministic filters the model is not trusted with: an empty bullet,
 *     `tooShort` (§4.4 granularity), `hedgeDominated` (§4.4), then any surviving `CheckBullet`
 *     violation (banned term, cliché, a house-style pattern the normalizer could not fix, a hedge).
 *
 * The violations are returned too, because they are what the re-ask message is built from. */
export function PostPassBullet(record: {
	selfContainedText: string,
	eventType: BulletEventType,
	oddityCount: number,
	filterReason: BulletFilterReason|n,
}, checkOptions: CheckBulletOptions): {record: Omit<IndexedBullet, "bulletId"|"attempts">, repairable: boolean} {
	const normalized = NormalizeHouseStyle(record.selfContainedText);

	if (record.filterReason != null) {
		// keep the model's verdict; still normalise the text so the Corpus tab shows house style
		return {record: {...record, selfContainedText: normalized}, repairable: false};
	}

	// hedge-dominance MUST be measured on the text as the model wrote it: `NormalizeHouseStyle`
	// strips the hedges themselves, so measuring after normalisation can never see them (a bullet
	// made entirely of "I think I might have..." then looks like a clean short event).
	// Hedge-dominated text cannot be salvaged by a rewrite request either -- the bullet's content IS
	// the hedging -- so this is a hard verdict, checked before anything else.
	if (IsHedgeDominated(record.selfContainedText)) {
		return {record: {...record, selfContainedText: normalized, filterReason: "hedgeDominated"}, repairable: false};
	}

	if (normalized.trim().length == 0) {
		return {record: {...record, selfContainedText: normalized, filterReason: "empty"}, repairable: false};
	}

	if (CountWords(normalized) < (checkOptions.minWords ?? 6)) {
		return {record: {...record, selfContainedText: normalized, filterReason: "tooShort"}, repairable: false};
	}

	const violations = CheckBullet(normalized, checkOptions);
	if (violations.length == 0) {
		return {record: {...record, selfContainedText: normalized, filterReason: null}, repairable: false};
	}

	// violations that remain after normalisation are worth ONE re-ask: a banned term can be written
	// around, a house-style pattern can be rephrased. A `tooShort`/`empty` verdict already returned.
	return {
		record: {
			...record,
			selfContainedText: normalized,
			filterReason: "styleViolation",
			postPassViolations: violations.map(a=>`${a.kind}: ${a.message}`),
		},
		repairable: true,
	};
}

// the per-dream driver
// ==========

/** Indexes ONE dream's bullets (§4.1-§4.4, §7): call -> validate -> post-pass -> at most one re-ask.
 *
 * Returns a record for every input bullet, always. A bullet the model never produced, or whose text
 * could not be made clean even after the re-ask, still gets a record -- marked `indexFailed` or
 * `styleViolation` respectively -- so a dream never silently contributes fewer bullets than it has.
 * That matters for the §17 supply check: a silent drop is invisible, an explicit filter reason is not. */
export async function IndexDreamBullets(backend: LLMBackend, dreamBullets: SourceBullet[], options: IndexOptions = {}): Promise<IndexDreamResult> {
	const dreamId = dreamBullets[0]?.dreamId ?? "(empty)";
	const maxAttempts = Math.max(1, options.maxAttempts ?? 2);
	const checkOptions: CheckBulletOptions = {
		bannedWords: options.bannedWords ?? [],
		blacklist: options.blacklist ?? [],
	};

	if (dreamBullets.length == 0) {
		return {dreamId, bullets: [], failed: false, outputTokens: 0, attempts: 0};
	}

	/** the best record per ordinal so far (a later attempt overwrites an earlier one) */
	const resolved = new Map<number, Omit<IndexedBullet, "bulletId"|"attempts">>();
	/** how many attempts each ordinal needed */
	const attemptsByOrdinal = new Map<number, number>();
	/** ordinals still worth asking about */
	const pending = new Set<number>([...Array(dreamBullets.length).keys()]);

	let totalTokens = 0;
	let attemptCount = 0;
	let lastReply: string|n = null;
	let lastError: string|n = null;
	let fixNotes: string|n = null;

	for (let attempt = 0; attempt < maxAttempts; attempt++) {
		if (pending.size == 0) break;

		const result: CallLLMResult = await CallLLM(backend, {
			role: "indexer",
			model: options.model ?? null,
			system: Index_SystemPrompt(),
			user: Index_UserPrompt({
				dreamId,
				bullets: dreamBullets,
				bannedWords: options.bannedWords,
				blacklist: options.blacklist,
				fixNotes,
			}),
			json: Index_JSONSchema(),
			counter: options.counter ?? null,
		});
		attemptCount++;
		totalTokens += result.outputTokens;
		lastReply = result.text;

		if (result.error != null) {
			lastError = result.error;
			continue; // CallLLM already retried internally; this is a fresh, cheaper re-ask with notes
		}

		const parsed = ParseIndexReply(result.parsed, dreamBullets.length);
		const problems = [...parsed.problems];

		// post-pass each record we got, and collect the ones that need a re-ask
		const repairableOrdinals: number[] = [];
		for (const [ordinal, rawRecord] of parsed.byOrdinal.entries()) {
			const {record: postPassed, repairable} = PostPassBullet(rawRecord, checkOptions);
			const isNew = !resolved.has(ordinal);
			// only accept a record that is better than what we already have: a re-ask that comes back
			// worse must not erase an earlier usable result
			if (isNew || repairable == false || resolved.get(ordinal)?.filterReason == "styleViolation") {
				resolved.set(ordinal, postPassed);
				attemptsByOrdinal.set(ordinal, attempt + 1);
			}
			if (repairable) {
				repairableOrdinals.push(ordinal);
				problems.push(
					`Bullet ${ordinal}: \`selfContainedText\` still breaks the house style after normalisation -- `
					+ (postPassed.postPassViolations ?? []).join("; "),
				);
			} else {
				pending.delete(ordinal);
			}
		}

		if (repairableOrdinals.length == 0) {
			// nothing repairable left; `pending` may still hold ordinals the model never produced, which
			// the next attempt must supply
			if (problems.length == 0) break;
		}

		lastError = problems.length > 0 ? problems.join("\n") : null;
		fixNotes = lastError;
	}

	// assemble: one record per input bullet, in input order
	const bullets: IndexedBullet[] = dreamBullets.map((sourceBullet, ordinal)=>{
		const record = resolved.get(ordinal);
		if (record == null) {
			return {
				bulletId: sourceBullet.id,
				selfContainedText: NormalizeHouseStyle(sourceBullet.rawText),
				eventType: "other" as BulletEventType,
				oddityCount: 0,
				filterReason: "indexFailed" as BulletFilterReason,
				attempts: attemptCount,
			};
		}
		return {
			bulletId: sourceBullet.id,
			...record,
			attempts: attemptsByOrdinal.get(ordinal) ?? attemptCount,
		};
	});

	const unfinished = bullets.filter(a=>a.filterReason == "indexFailed");
	const failed = unfinished.length > 0;

	return {
		dreamId,
		bullets,
		failed,
		error: failed
			? `No usable reply for ${unfinished.length}/${bullets.length} bullet(s) after ${attemptCount} attempt(s). Last problem: ${lastError ?? "(none reported)"}`
			: null,
		lastReply,
		outputTokens: totalTokens,
		attempts: attemptCount,
	};
}

/** Convenience: the index pass for a list of dreams, sequentially (so a cancel button in the Live
 * wrapper has a natural checkpoint between dreams, and so a rate limit costs at most one dream). */
export async function IndexDreams(
	backend: LLMBackend,
	dreamBullets: SourceBullet[][],
	options: IndexOptions = {},
	onDreamDone?: (result: IndexDreamResult, index: number, total: number)=>void,
): Promise<IndexDreamResult[]> {
	const results: IndexDreamResult[] = [];
	for (const [index, bullets] of dreamBullets.entries()) {
		if (bullets.length == 0) continue;
		const result = await IndexDreamBullets(backend, bullets, options);
		results.push(result);
		onDreamDone?.(result, index, dreamBullets.length);
	}
	return results;
}

// summary (the §17 supply check)
// ==========

export type IndexSummary = {
	bulletCount: number;
	usableCount: number;
	/** filtered count per reason, so an over-eager filter shows up immediately */
	byFilterReason: Record<string, number>;
	/** usable count per event type -- the §5.3 supply distribution fakes must match */
	byEventType: Record<string, number>;
	/** mean `oddityCount` over usable bullets -- what the §5.2 concept-count sampler draws from */
	meanOddityCount: number;
};

/** Summarises an index run. This is the §17 "supply of usable bullets after filtering" check, and
 * the source of the event-type / oddity distributions that P6's fake generator needs (§5.2, §5.3). */
export function SummarizeIndexedBullets(bullets: IndexedBullet[]): IndexSummary {
	const byFilterReason: Record<string, number> = {};
	const byEventType: Record<string, number> = {};
	let usableCount = 0;
	let odditySum = 0;

	for (const bullet of bullets) {
		if (bullet.filterReason != null) {
			byFilterReason[bullet.filterReason] = (byFilterReason[bullet.filterReason] ?? 0) + 1;
			continue;
		}
		usableCount++;
		byEventType[bullet.eventType] = (byEventType[bullet.eventType] ?? 0) + 1;
		odditySum += bullet.oddityCount;
	}

	return {
		bulletCount: bullets.length,
		usableCount,
		byFilterReason,
		byEventType,
		meanOddityCount: usableCount > 0 ? odditySum / usableCount : 0,
	};
}

/** Turns raw extracted bullets into the `[dream, bullets]` list `IndexDreams` wants, skipping
 * bullets that P4's checker already rejects outright -- no point paying a model to index material
 * that can never be used.
 *
 * A bullet is pre-skipped when: it is empty, it is hedge-dominated, or `CheckBullet` on its RAW text
 * reports only problems that normalisation cannot fix. (Banned terms and house-style patterns are NOT
 * pre-skipped: the index pass's rewrite is exactly what fixes those, so filtering them here would
 * discard usable material.) */
export function PrepareBulletsForIndexing(bullets: SourceBullet[], checkOptions: CheckBulletOptions): SourceBullet[][] {
	const usable = bullets.filter(bullet=>{
		if (bullet.rawText.trim().length == 0) return false;
		if (IsHedgeDominated(bullet.rawText)) return false;

		const rawViolations = CheckBullet(bullet.rawText, checkOptions);
		// `tooShort` on the raw text can still become long enough after the standalone rewrite? No --
		// a standalone rewrite does not add material, so a too-short bullet stays too short.
		if (rawViolations.some(a=>a.kind == "tooShort" || a.kind == "empty")) return false;
		return true;
	});

	return [...GroupBulletsByDream(usable).values()];
}

/** Truncates a model reply for logging. Re-exported so the Live wrapper and the Corpus tab agree. */
export function PreviewIndexReply(text: string|n): string {
	return text == null ? "(no reply)" : PreviewText(text, 500);
}
